/**
 * SUPPORT — migration guide Bölüm 3.12 SupportController.
 *
 * Request shapes for all four routes confirmed via live swagger + curl:
 *   - support/AddSupport and support/GetSupports share one request DTO
 *     (SupportRequest): { SessionHeader, Id, Subject, ShopCartID, SupportType, Message }.
 *   - support/ReplySupport: { SessionHeader, SupportId, ReplyMessage }.
 *   - support/SupportCloseOpen: { SessionHeader, SupportId, IsClosed }.
 * Curl against AddSupport (with a valid SupportRequest body) returned a structured
 * business response `{ Supports: [], HasError: true, ServiceError: { ErrorMessages:
 * ["ShoppingCart Id kontrol ediniz."] } }` — confirming both the request shape AND
 * that the response wrapper's list field is `Supports` (→ `supports` camelCased).
 * Also confirms ShopCartID is validated as required even for a plain "Other" ticket in
 * this backend, so the create form below asks for it.
 *
 * GetSupports itself 500s ("An error has occurred.") against this test account
 * regardless of body shape (bare session, full SupportRequest, with/without
 * SupportType — all 500 identically) — this is a backend-side issue for this test
 * account's data, not a request-shape problem (AddSupport, using the same SupportRequest
 * shape and session, returns a clean structured response instead). The UI below still
 * wires GetSupports for real and surfaces its error via ApiErrorAlert like any other
 * query — nothing to work around on the frontend.
 *
 * The ticket entity's exact response fields are NOT confirmed (GetSupports never
 * returned real data in testing) — SupportTicket below is a best-effort superset of
 * the request DTO's fields plus commonly-expected additions (isClosed/insertedAt/
 * replies). Render defensively (optional chaining, `— ` fallbacks) throughout the UI.
 */
import type { ResponseBase } from './common';

export type SupportType = 'TicketRefund' | 'TicketCancel' | 'TicketChange' | 'Accounting' | 'Other';

export const SUPPORT_TYPE_LABELS: Record<SupportType, string> = {
  TicketRefund: 'Bilet İadesi',
  TicketCancel: 'Bilet İptali',
  TicketChange: 'Bilet Değişikliği',
  Accounting: 'Muhasebe',
  Other: 'Diğer',
};

/** Best-effort — real field name/shape unconfirmed, see file header. */
export interface SupportReply {
  id?: string;
  replyMessage?: string;
  message?: string;
  insertedAt?: string;
  isAgencySide?: boolean;
}

/** Best-effort — real field name/shape unconfirmed, see file header. */
export interface SupportTicket {
  id?: string;
  subject?: string;
  shopCartID?: string;
  supportType?: SupportType;
  message?: string;
  isClosed?: boolean;
  insertedAt?: string;
  replies?: SupportReply[];
}

export interface SupportRequest {
  id?: string;
  subject?: string;
  shopCartID?: string;
  supportType?: SupportType;
  message?: string;
}

export interface SupportListResponse extends ResponseBase {
  supports?: SupportTicket[] | null;
}

export interface SupportReplyRequest {
  supportId: string;
  replyMessage: string;
}

export interface SupportCloseOpenRequest {
  supportId: string;
  isClosed: boolean;
}
